<template>
  <v-skeleton-loader
    :loading="showSkeletonLoader"
    :type="coronetSkeletonLoaderTypes.USERS_SETTINGS"
  >
    <table-wrapper class="mt-3">
      <v-expansion-panels flat hide-actions multiple v-model="expandedPanels">
        <v-expansion-panel
          v-for="(config, policyType) in policiesConfig"
          :key="policyType"
          :value="policyType"
          :readonly="!policiesByType[policyType].length"
          :class="{ 'policy-panel--empty': !policiesByType[policyType].length }"
          :data-testid="`device-policy-group-${policyType}`"
        >
          <template #title>
            <v-icon
              v-if="policiesByType[policyType].length"
              icon="$chevronDown"
              class="mr-3 chevron"
              :class="{ rotated: isExpanded(policyType) }"
            />
            <div v-else class="mr-3 chevron-placeholder" />
            <v-icon size="40" class="mr-3" :icon="config.icon" />
            <span class="subtitle1">
              {{ $t(`devicesSettings.devicePolicyTab.policyTypes.${policyType}`) }}
            </span>
            <v-spacer />
            <span
              class="body2 text--semitransparent"
              :data-testid="`device-policy-count-${policyType}`"
            >
              {{ policyCountLabel(policyType) }}
            </span>
          </template>
          <template #text>
            <v-table v-if="policiesByType[policyType].length" class="white">
              <thead>
                <tr>
                  <th v-for="col in config.headers" :key="col.key" class="text-left caption">
                    {{ $t(col.labelKey) }}
                  </th>
                  <th class="text-left" :aria-label="$t('ariaLabels.actionsColumn')"></th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(policy, i) in policiesByType[policyType]"
                  :key="policy.tableItemId"
                  :data-testid="`device-policy-row-${i}`"
                >
                  <td v-for="col in config.headers" :key="col.key">
                    <span v-if="col.cell === 'text'" class="body2">{{
                      policy.display[col.key]
                    }}</span>
                    <div v-else-if="col.cell === 'paths'" class="d-flex align-center path-cell">
                      <span
                        class="body2 text-truncate path-cell__value"
                        :data-testid="`device-policy-path-${i}`"
                      >
                        {{ policy.monitoredPaths?.[0] }}
                      </span>
                      <v-tooltip
                        v-if="(policy.monitoredPaths?.length ?? 0) > 1"
                        location="top"
                        content-class="policy-overflow-tooltip"
                      >
                        <template #activator="{ props }">
                          <span
                            v-bind="props"
                            class="body2 ml-1 overflow-indicator"
                            :data-testid="`device-policy-paths-overflow-${i}`"
                          >
                            +{{ (policy.monitoredPaths?.length ?? 0) - 1 }}
                          </span>
                        </template>
                        <div class="d-flex flex-column">
                          <div
                            v-for="path in policy.monitoredPaths"
                            :key="path"
                            class="body2 text-white"
                          >
                            {{ path }}
                          </div>
                        </div>
                      </v-tooltip>
                    </div>
                    <div v-else class="d-flex align-center label-container">
                      <template v-if="policy.labels.length > 0">
                        <v-chip
                          v-for="label in policy.labels.slice(0, 2)"
                          :key="label.id"
                          variant="flat"
                          rounded="sm"
                          size="default"
                          :color="label.color"
                          class="label-item h-100 py-0 px-1 flex-shrink-0"
                        >
                          <span class="coro-label text-uppercase text-primary">
                            {{ label.name }}
                          </span>
                        </v-chip>
                        <v-tooltip
                          v-if="policy.labels.length > 2"
                          location="top"
                          content-class="policy-overflow-tooltip"
                        >
                          <template #activator="{ props }">
                            <span
                              v-bind="props"
                              class="body2 overflow-indicator"
                              :data-testid="`device-policy-labels-overflow-${i}`"
                            >
                              +{{ policy.labels.length - 2 }}
                            </span>
                          </template>
                          <div class="d-flex flex-column">
                            <div
                              v-for="label in policy.labels"
                              :key="label.id"
                              class="body2 text-white"
                            >
                              {{ label.name }}
                            </div>
                          </div>
                        </v-tooltip>
                      </template>
                      <v-chip
                        v-else
                        variant="flat"
                        size="default"
                        label
                        rounded="3"
                        :data-testid="`device-policy-all-devices-${i}`"
                      >
                        <span class="coro-label text-uppercase text-primary">
                          {{ $t("devicesSettings.devicePolicyTab.allDevices") }}
                        </span>
                      </v-chip>
                    </div>
                  </td>
                  <td class="text-right" @click="$event.stopPropagation()">
                    <v-menu bottom left>
                      <template v-slot:activator="{ props }">
                        <v-icon
                          icon="$dots"
                          class="item-clickable"
                          v-bind="props"
                          :data-testid="`device-policy-action-btn-${i}`"
                        />
                      </template>
                      <v-list>
                        <v-list-item
                          v-for="(action, index) in actions"
                          :key="index"
                          :value="index"
                          :data-testid="`device-policy-menu-item-${i}-${action}`"
                          @click="onActionClick(action, policy)"
                        >
                          <v-list-item-title>
                            {{ $t(`devicesSettings.devicePolicyTab.actions.${action}`) }}
                          </v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </td>
                </tr>
              </tbody>
            </v-table>
          </template>
        </v-expansion-panel>
      </v-expansion-panels>
    </table-wrapper>
  </v-skeleton-loader>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType, ref } from "vue";
import { useI18n } from "vue-i18n";
import TableWrapper from "@/components/TableWrapper.vue";
import { coronetSkeletonLoaderTypes } from "@/constants/skeleton-loader";
import { DevicePolicyAction, DevicePolicyType } from "@/constants/devices";
import type { DevicePolicyAdaptedItem } from "@/_store/device-policy/device-policy-settings.module";

export type PolicyCellType = "text" | "labels" | "paths";

export interface PolicyColumn {
  key: string;
  labelKey: string;
  cell: PolicyCellType;
}

export interface PolicyConfig {
  icon: string;
  headers: PolicyColumn[];
}

export const policiesConfig: Record<DevicePolicyType, PolicyConfig> = {
  [DevicePolicyType.FILE_INTEGRITY]: {
    icon: "$fileDevice",
    headers: [
      { key: "os", labelKey: "devicesSettings.devicePolicyTab.os", cell: "text" },
      { key: "repeatEvery", labelKey: "devicesSettings.devicePolicyTab.repeatEvery", cell: "text" },
      { key: "path", labelKey: "devicesSettings.devicePolicyTab.filePath", cell: "paths" },
      {
        key: "labels",
        labelKey: "devicesSettings.devicePolicyTab.appliedToDevices",
        cell: "labels",
      },
    ],
  },
};

export default defineComponent({
  components: {
    TableWrapper,
  },
  props: {
    items: {
      type: Array as PropType<DevicePolicyAdaptedItem[]>,
      required: true,
    },
    showSkeletonLoader: {
      type: Boolean,
      default: false,
    },
    actions: {
      type: Array as PropType<DevicePolicyAction[]>,
      default: () => [DevicePolicyAction.EDIT, DevicePolicyAction.REMOVE],
    },
  },
  emits: ["action-click"],
  setup(props, { emit }) {
    const { t } = useI18n();

    const expandedPanels = ref<DevicePolicyType[]>([]);

    const policiesByType = computed<Record<string, DevicePolicyAdaptedItem[]>>(() => {
      const groups = Object.fromEntries(
        Object.keys(policiesConfig).map((policyType) => [policyType, []])
      ) as Record<string, DevicePolicyAdaptedItem[]>;
      props.items.forEach((item) => groups[item.policyType]?.push(item));
      return groups;
    });

    const isExpanded = (policyType: DevicePolicyType): boolean =>
      expandedPanels.value.includes(policyType);

    const policyCountLabel = (policyType: string): string => {
      const count = policiesByType.value[policyType].length;
      return count === 0
        ? t("devicesSettings.devicePolicyTab.noPolicies")
        : t("devicesSettings.devicePolicyTab.policiesQuantity", { n: count }, count);
    };

    const onActionClick = (action: DevicePolicyAction, policy: DevicePolicyAdaptedItem) => {
      emit("action-click", { action, item: policy });
    };

    return {
      coronetSkeletonLoaderTypes,
      policiesConfig,
      expandedPanels,
      policiesByType,
      isExpanded,
      policyCountLabel,
      onActionClick,
    };
  },
});
</script>

<style lang="scss" scoped>
.rotated {
  transform: rotate(180deg);
}

/* Keeps the type icon and title aligned with panels that still show a chevron. */
.chevron-placeholder {
  width: 24px;
}

.policy-panel--empty :deep(.v-expansion-panel-title) {
  cursor: default;
}

:deep(.v-expansion-panel) {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

:deep(.v-expansion-panel--active:not(:first-child)),
:deep(.v-expansion-panel--active + .v-expansion-panel) {
  margin-top: 0;
}

:deep(.v-expansion-panel-title) {
  min-height: 64px;
}

:deep(.v-expansion-panel-title--active) {
  border-bottom: 1px solid rgb(var(--v-theme-indigo-faint));
}

:deep(.v-expansion-panel-text__wrapper) {
  padding: 0;
}

.v-table :deep(.v-table__wrapper) > table > thead > tr > th:first-child,
.v-table :deep(.v-table__wrapper) > table > tbody > tr > td:first-child {
  padding-left: 60px;
}

.label-container {
  gap: 8px;
}

.path-cell {
  max-width: 280px;
}

.path-cell__value {
  min-width: 0;
}

.overflow-indicator {
  cursor: pointer;
  flex-shrink: 0;
}

.label-item {
  display: inline-block;
  overflow-wrap: initial;
}
</style>
